import type {
  InferConfigType,
  InferFinalResponseType,
  InferResponseType,
  MakeAllKeysRequired,
} from '../types';
import {CacheKeyConfig, createCacheKey} from './createCacheKey';
import {
  GrpcQueryConfigWithSection,
  GrpcRequestServiceMethod,
  useGenericGrpcRequestFn,
} from './useGenericGrpcRequest';
import {DEFAULT_QUERY_CONFIG, useQueryClient} from '../react-query';
import {useCallback} from 'react';
import {GrpcConfig, useGrpcClientConfig} from './GrpcClientContext';
import {DeepPartial} from 'ts-essentials';
import {generateCacheKey} from '../useRequest';
import {isUnimplementedError} from './code';
import {convertAllNullPropertiesToUndefined} from './convertAllNullPropertiesToUndefined';

export interface GenericTadaridaConfig {
  config?: DeepPartial<GrpcConfig>;
}

/**
 * This hook is used to create a callback that can be used to retrieve data
 * unlike the useGrpcRequestMutation hook, which is used to mutate data
 * @param maybeAsyncService
 * @param grpcQueryConfig
 * @returns
 */
export function useGrpcRequestCallback<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof Partial<GenericTadaridaConfig>>
  >,
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  MethodName extends ServiceMethod['method'],
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(maybeAsyncService: ServiceMethod, grpcQueryConfig?: GrpcConfigType) {
  const genericConfig = useGrpcClientConfig();

  const {
    uniqueIdentifier: uniqueIdentifierGenerated,
    transformCacheKey,
    ...queryConfig
  } = grpcQueryConfig ?? {};

  const fetch = useGenericGrpcRequestFn(
    maybeAsyncService,
    /*
      This is an empty object because we dont know the
      params on render yet, because of that we pass
      them while calling fetch
    */
    {},
    {config: genericConfig},
  );

  const queryClient = useQueryClient();

  return useCallback(
    (params?: ParamsType): Promise<TData> => {
      if (
        grpcQueryConfig?.resolveMockData !== 'never' &&
        grpcQueryConfig?.mockData
      ) {
        return new Promise<ResponseType>((resolve, reject) => {
          if (grpcQueryConfig?.resolveMockData === 'always') {
            grpcQueryConfig?.mockData?.(params).then(resolve);
          } else {
            fetch()
              .then(resolve)
              .catch(async error => {
                if (
                  isUnimplementedError(error) &&
                  grpcQueryConfig.resolveMockData === 'not-implemented'
                ) {
                  return grpcQueryConfig?.mockData?.(params).then(resolve);
                }
                return reject(error);
              });
          }
        });
      }

      const uniqueIdentifier =
        uniqueIdentifierGenerated ??
        (process.env.NODE_ENV === 'test' && 'method' in maybeAsyncService
          ? (maybeAsyncService as ServiceMethod).method
          : undefined);
      const cacheKeyConfig = {
        uniqueIdentifier,
        params,
      } as CacheKeyConfig;
      const key = createCacheKey(
        transformCacheKey ? transformCacheKey(cacheKeyConfig) : cacheKeyConfig,
      );

      return queryClient.fetchQuery(
        generateCacheKey(key),
        () =>
          fetch(
            convertAllNullPropertiesToUndefined<ParamsType | undefined>(params),
          ),
        {
          ...DEFAULT_QUERY_CONFIG,
          ...queryConfig,
        },
      );
    },
    [
      queryClient,
      fetch,
      queryConfig,
      transformCacheKey,
      maybeAsyncService,
      uniqueIdentifierGenerated,
      grpcQueryConfig?.mockData,
      grpcQueryConfig?.resolveMockData,
    ],
  );
}

/**
 * This is a raw version of the useGrpcRequestCallback hook which does not use react-query and CAN BE called on the server
 */
export function useRawGrpcRequestCallback<
  ServiceMethod extends GrpcRequestServiceMethod,
  ParamsType extends MakeAllKeysRequired<
    Omit<Parameters<ServiceMethod>[1], keyof Partial<GenericTadaridaConfig>>
  >,
  ResponseType extends InferResponseType<ReturnType<ServiceMethod>>,
  MethodName extends ServiceMethod['method'],
  DefaultConfigType extends GrpcQueryConfigWithSection<
    ResponseType,
    ParamsType
  >,
  GrpcConfigType extends InferConfigType<
    MethodName,
    ResponseType,
    DefaultConfigType
  >,
  TData extends object = InferFinalResponseType<GrpcConfigType, ResponseType>,
>(maybeAsyncService: ServiceMethod, grpcQueryConfig?: GrpcConfigType) {
  const genericConfig = useGrpcClientConfig();

  const {
    uniqueIdentifier: uniqueIdentifierGenerated,
    transformCacheKey,
    ...queryConfig
  } = grpcQueryConfig ?? {};

  const fetch = useGenericGrpcRequestFn(
    maybeAsyncService,
    /*
      This is an empty object because we dont know the
      params on render yet, because of that we pass
      them while calling fetch
    */
    {},
    {config: genericConfig},
  );

  return useCallback(
    (params?: ParamsType): Promise<TData> => {
      return fetch(
        convertAllNullPropertiesToUndefined<ParamsType | undefined>(params),
      );
    },
    [
      fetch,
      queryConfig,
      transformCacheKey,
      maybeAsyncService,
      uniqueIdentifierGenerated,
      grpcQueryConfig?.mockData,
      grpcQueryConfig?.resolveMockData,
    ],
  );
}
